import { api } from '@/app'
import { CommunityFeed } from '@/types'
import { getListQueryParams, useQueryList } from '@/utils'
import { createQueryManager, createQueryOperations } from '@codeleap/query'
import { waitFor } from '@codeleap/utils'
import { queryClient } from '../queryClient'
import { cursorStore, filtersStore } from './stores'
import { useMemo } from 'react'
import { TypeGuards } from '@codeleap/types'
import { achievementsStore } from '@/stores'

type CommunityFilters = {
  starting?: CommunityFeed['id']
}

type CursorPaginationResponse<T> = {
  next_cursor: string | null
  previous_cursor: string | null
  count: number
  results: T[]
}

const BASE_URL = 'community/v2/'

const listFeedItems = async (limit: number, direction: string, providedFilters = {}) => {
  let filters = { ...providedFilters }

  let cursor: string

  if (['nextCursor', 'previousCursor'].includes(direction)) {
    cursor = cursorStore.get()?.[direction]
  }

  if (filters) {
    filters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (!value) return acc
      acc[key] = key === 'starting' ? value : getListQueryParams(value as string[])
      return acc
    }, {})
  }

  const response = await api.get<CursorPaginationResponse<CommunityFeed>>(`${BASE_URL}`, {
    params: {
      limit,
      cursor,
      ...filters
    }
  })

  return response.data
}

const retrieveFeedItem = async (id: CommunityFeed['id']) => {
  const response = await api.get<CommunityFeed>(`${BASE_URL}${id}/`)

  return response.data
}

export const communityManager = createQueryManager<CommunityFeed, CommunityFilters>({
  name: 'community',
  queryClient: queryClient.client,

  async listFn(limit, offset, filters) {
    const list = listFeedItems(limit, String(offset), filters)
    const startingRequest =
      filters?.starting && !offset ? retrieveFeedItem(filters?.starting) : Promise.resolve<null>(null)

    const responses = await Promise.all([list, startingRequest.catch(() => {})])

    const listResponse = responses[0]
    const itemResponse = responses[1]

    const results = listResponse.results

    if (itemResponse) {
      results.unshift(itemResponse)
    }

    cursorStore.set({
      nextCursor: listResponse.next_cursor,
      previousCursor: listResponse.previous_cursor
    })

    return results
  },
  async retrieveFn(id: CommunityFeed['id']) {
    return retrieveFeedItem(id)
  }
})

export const communityOperations = createQueryOperations({
  queryClient: queryClient.client
})
  .mutation('toggleLike', async (data: { postId: CommunityFeed['id']; to: boolean }) => {
    const response = await api.patch(`${BASE_URL}${data?.postId}/like/`, {
      liked: data?.to
    })

    achievementsStore.verifyProgress(response?.data?.achievement_progress)
  })
  .mutation('share', async (postId: CommunityFeed['id']) => {
    const item = communityManager.queryKeys.getRetrieveData(postId, { deepSearch: true })

    if (!item) return

    try {
      communityManager.mutations.updateItems({
        ...item,
        shares: item.shares + 1
      })

      const response = await api.patch(`community/${postId}/share/`, {})

      achievementsStore.verifyProgress(response?.data?.achievement_progress)
    } catch (err) {
      communityManager.mutations.updateItems(item)
      logger.error(err)
    }
  })
  .mutation('updateCommentCount', async (data: { postId: CommunityFeed['id']; value?: number }) => {
    const item = communityManager.queryKeys.getRetrieveData(data?.postId, { deepSearch: true })

    if (!item) return

    communityManager.mutations.updateItems({
      ...item,
      comments: Math.max(0, (item.comments || 0) + (data?.value < 0 ? -Math.abs(data?.value) : Math.abs(data?.value)))
    })
  })
  .mutation('report', async (data: { postId: CommunityFeed['id']; reason: string }) => {
    const response = await api.post('crm/report/', {
      reason: data?.reason,
      feed_item: data?.postId
    })

    return response.data
  })

export function useCommunity(starting: CommunityFeed['id']) {
  const filters = filtersStore.use()

  const communityFilters = useMemo(() => {
    const rowFilters = {
      ...filters,
      starting
    }

    return Object.entries(rowFilters).reduce((acc, [key, value]) => {
      const hasValue = TypeGuards.isArray(value) ? value?.length > 0 : !TypeGuards.isNil(value)
      if (hasValue) acc[key] = value
      return acc
    }, {})
  }, [filters, starting])

  const community = communityManager.useList({
    filters: communityFilters,
    initialPageParam: undefined as any,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    getNextPageParam() {
      return 'nextCursor' as any
    },
    getPreviousPageParam() {
      return 'previousCursor' as any
    }
  })

  const listProps = useQueryList<CommunityFeed, any>(community)

  return { community, listProps }
}

export async function markSeen(posts: CommunityFeed['id'][]) {
  const response = await api.post(`${BASE_URL}mark_seen/`, { posts })

  achievementsStore.verifyProgress(response?.data?.achievement_progress)
}

export async function refreshFeed() {
  await api.post(`${BASE_URL}refresh/`, {})

  await waitFor(250)

  cursorStore.set({ nextCursor: null, previousCursor: null })

  queryClient.client.refetchQueries({
    queryKey: communityManager.queryKeys.keys.list,
    type: 'all',
    predicate(query) {
      const relevant = query.queryKey.slice(0, 2)
      return relevant.every((v, i) => communityManager.queryKeys.keys.list[i] == v)
    }
  })
}

export const retrieveCommunityFilterOptions = async () => {
  const response = await api.get(`${BASE_URL}feed_filters/`)

  return response.data
}
